import { useTranslation } from 'react-i18next';

import { Button, Flex, spaces, Text } from '@electricjs/arc';

import { MultiLineEllipsisText } from 'common';

import { Store } from '@/types/storefront';
import { Product } from '@/types/products';
import { StorefrontAsset } from '@/types/assets';
import formatCurrency from '@/helpers/formatCurrency';

import { ItemImage } from './ItemImage';

type CatalogMiniListRow = {
  store: Store.Catalog;
  item: Product;
};

type AssetMiniListRow = {
  store: Store.Asset;
  item: StorefrontAsset;
};

type ItemMiniListRowProps = (CatalogMiniListRow | AssetMiniListRow) & {
  id: string;
  selected: boolean;
  onAddItem: () => void;
  onRemoveItem: () => void;
};

export const ItemMiniListRow = ({
  id,
  store,
  item,
  selected,
  onAddItem,
  onRemoveItem,
}: ItemMiniListRowProps) => {
  const { t } = useTranslation('storefront');
  return (
    <Flex
      id={id}
      width="100%"
      vAlignContent="center"
      columnGap={spaces.space_m}
      padding={spaces.space_xs}>
      {/* Left-side: image (if catalog product) */}
      {store === Store.Catalog && (
        <ItemImage
          flexShrink="0"
          src={item.image}
          alt={item.name}
          size="cartSize" // same size used on cart
        />
      )}
      {/* Right-side: 2 rows */}
      <Flex vertical flexGrow="1" rowGap={spaces.space_xs}>
        {/* Row 1 (name) */}
        <MultiLineEllipsisText variant="body-2" $maxLines={2} title={item.name}>
          {item.name}
        </MultiLineEllipsisText>

        {/* Row 2 (price and button) */}
        <Flex
          width="100%"
          justifyContent={
            store === Store.Catalog ? 'space-between' : 'flex-end'
          }
          alignItems="center">
          {store === Store.Catalog && (
            <Text variant="label-large">
              {formatCurrency(item.price, item.currencyCode)}
            </Text>
          )}
          {!selected ? (
            <Button
              id={`${id}-add-to-cart-button`}
              onClick={onAddItem}
              buttonSize="small">
              {t('common.addToCart')}
            </Button>
          ) : (
            <Button
              id={`${id}-remove-from-cart-button`}
              onClick={onRemoveItem}
              buttonSize="small"
              variant="outline"
              iconAfter="trashcan">
              {t('common.remove')}
            </Button>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};
